/* eslint-disable camelcase */
import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react'
import style from './ManageProfile.module.scss'
import '../../style/roleContianer.scss'
import {
  AuthService,
  PortalService,
  PortalType,
  UserService,
  UserType
} from '@digitalworkflow/dwloginclient'
import { ApiResultType } from '@digitalworkflow/dwloginclient/lib/Auth/ApiResultType'
import { Button, Spinner } from 'reactstrap'
import { toast } from 'react-toastify'
import ToggleButton from '../ToggleButton/ToggleButton'
import { sortComparator } from '../../utils/sortArray'
import LoginPortalGrid from '../LoginPortalGrid/LoginPortalGrid'
import { compareArrays } from '../../utils/compareArray'
interface IPortals {
  profile: UserType | null
  getUser: () => void
  isAllowedToModify?: boolean
}
type SortDirection = 'asc' | 'desc' | null

const Portals = ({ profile, getUser, isAllowedToModify = true }: IPortals) => {
  const [enabledPortal, setEnabledPortal] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const authService = AuthService.instance()
  PortalService.setAuthServiceInstance(authService)
  const portalService = new PortalService()
  const userService = new UserService()
  UserService.setAuthServiceInstance(authService)

  const portalRef = useRef<PortalType[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [bulkActionToggle, setBulkActionToggle] = useState<boolean>(false)

  const isDisabled = useMemo(() => {
    return compareArrays(
      (profile?.portals_enabled as string[]) ?? [],
      enabledPortal
    )
  }, [enabledPortal, profile])
  const columnDefs = useMemo(() => {
    return [
      {
        field: 'portal_name',
        headerName: 'Portal Name',
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        minWidth: 140,
        flex: 2,
        floatingFilter: true,
        comparator: sortComparator(null)
      },
      ...(isAllowedToModify
        ? [
            {
              field: 'action',
              headerName: 'Action',
              filter: 'agNumberColumnFilter',
              minWidth: 140,
              flex: 1,
              resizable: true,
              floatingFilter: true,
              floatingFilterComponent: ToggleButton,
              floatingFilterComponentParams: {
                suppressFilterButton: true,
                isChecked: bulkActionToggle,
                onChange: (e: any) => handleBulkActionToggle(e.target.checked)
              },
              sort: 'desc' as SortDirection,
              valueGetter: (params: any) =>
                enabledPortal.includes(params.data.id),
              cellRenderer: (params: any) => (
                <div
                  className={`flex justify-between items-center ${style.actionContainer}`}
                >
                  <ToggleButton
                    name='portal'
                    isChecked={enabledPortal.includes(params.data.id)}
                    onChange={(event: any) =>
                      handleToggleActive(params.data.id, event.target.checked)
                    }
                  />
                </div>
              )
            }
          ]
        : [])
    ]
  }, [isAllowedToModify, enabledPortal])

  useEffect(() => {
    setEnabledPortal(profile?.portals_enabled ?? [])
  }, [profile])

  const handleBulkActionToggle = (value: boolean) => {
    setBulkActionToggle(value)
    const _allPortalIds = portalRef.current.map(
      (data: PortalType) => data.id
    ) as string[]
    if (value) {
      setEnabledPortal(_allPortalIds)
    } else {
      setEnabledPortal([])
    }
  }

  useEffect(() => {
    getPortals()
  }, [])
  const handleToggleActive = useCallback(
    (id: string, isActive: boolean) => {
      if (isActive) {
        setEnabledPortal((portal) => [...portal, id])
      } else {
        setEnabledPortal((portal) => portal.filter((r) => r !== id))
      }
    },
    [enabledPortal]
  )

  const handleUpdate = useCallback(async () => {
    setLoading(true)
    const _result: ApiResultType<UserType> = await userService.updateUserById(
      profile?.id ?? '',
      {
        ...profile,
        portals_enabled: enabledPortal
      }
    )
    if (!_result.is_error) {
      toast.success('Portals have been successfully updated')
    } else {
      toast.error(_result.message)
    }
    getUser()
    setLoading(false)
  }, [enabledPortal, setLoading])

  const getPortals = useCallback(async () => {
    try {
      setIsLoading(true)
      const _result: ApiResultType<PortalType[]> =
        await portalService.getAllPortals('restricted')
      if (!_result.is_error) {
        const data = _result.data ?? []
        if (data.length > 0) {
          const sortData = [...data].sort(sortComparator('portal_name'))
          portalRef.current = sortData
        } else {
          portalRef.current = []
        }

        if (
          profile?.portals_enabled &&
          profile?.portals_enabled.length > 0 &&
          profile?.portals_enabled.length === portalRef.current.length
        ) {
          handleBulkActionToggle(true)
        }
      }
      setIsLoading(false)
    } catch (e) {
      console.log(e)
    }
  }, [profile, setIsLoading])

  const portalsData = useMemo(() => {
    return isAllowedToModify
      ? portalRef.current
      : portalRef.current.filter((item: PortalType) =>
          enabledPortal.includes(item.id ?? '')
        )
  }, [isAllowedToModify, enabledPortal, portalRef.current])

  return (
    <div className={style.portalContainer}>
      {isLoading ? (
        <div className='flex justify-center'>
          <Spinner />
        </div>
      ) : (
        <div className={style.gridPortalContainer}>
          {isAllowedToModify && (
            <div className='mb-2 mt-2 d-flex justify-content-end'>
              <div className='d-flex gap-2'>
                <Button
                  type='submit'
                  color='delete'
                  className='btn-sm'
                  onClick={handleUpdate}
                  disabled={isDisabled}
                >
                  <i className='fal fa-edit me-2' />
                  Update
                  {loading && <Spinner className={style.spinner} />}
                </Button>
                <Button
                  type='button'
                  color='edit'
                  className='btn-sm'
                  disabled={isDisabled}
                  onClick={() =>
                    setEnabledPortal(profile?.portals_enabled ?? [])
                  }
                >
                  <i className='fa fa-times me-2' />
                  Cancel
                </Button>
              </div>
            </div>
          )}
          <LoginPortalGrid columnDefs={columnDefs} rowData={portalsData} />
        </div>
      )}
    </div>
  )
}
export default Portals
